// Open a printable "What we have so far" review sheet in a new window.
// Used from both the complete form and the step-by-step wizard so an FD can
// hand the family a hard-copy to confirm before burning a credit on AI generation.

const REVIEW_SECTIONS = [
  {
    title: 'Name',
    rows: [
      ['firstName',  'First name'],
      ['middleName', 'Middle name'],
      ['lastName',   'Last name'],
      ['nickname',   'Nickname / preferred name'],
    ],
  },
  {
    title: 'Dates & age',
    rows: [
      ['dateOfBirth', 'Date of birth', 'date'],
      ['dateOfDeath', 'Date of passing', 'date'],
      ['age',         'Age'],
    ],
  },
  {
    title: 'Home',
    rows: [
      ['homeCity',   'Home city'],
      ['homeState',  'Home state'],
      ['birthplace', 'Birthplace / hometown'],
    ],
  },
  {
    title: 'Life',
    rows: [
      ['occupation',     'Occupation'],
      ['education',      'Education'],
      ['militaryService','Military service'],
      ['hobbies',        'Hobbies & interests'],
    ],
  },
  {
    title: 'Achievements',
    rows: [
      ['achievements', 'Achievements & accomplishments'],
    ],
  },
  {
    title: 'Family',
    rows: [
      ['survivedBy',  'Survived by'],
      ['precededBy',  'Preceded in death by'],
    ],
  },
  {
    title: 'Service',
    rows: [
      ['serviceDate',     'Service date', 'date'],
      ['serviceLocation', 'Service location'],
    ],
  },
  {
    title: 'Personal touches',
    rows: [
      ['favoriteQuote',   'Favorite quote or saying'],
      ['personalMessage', 'Personal message'],
    ],
  },
];

const escapeHtml = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

export function buildReviewHtml(formData, options = {}) {
  const data = formData || {};
  const brand = (options.brand || '').trim();
  const deceased = [data.firstName, data.middleName, data.lastName].filter(Boolean).join(' ').trim();
  const nickname = (data.nickname || '').trim();
  const title = deceased
    ? `Obituary details for ${deceased}${nickname ? ` ("${nickname}")` : ''}`
    : 'Obituary details for review';

  const sectionsHtml = REVIEW_SECTIONS.map((section) => {
    const rows = section.rows
      .map(([key, label, kind]) => {
        const raw = data[key];
        const val =
          kind === 'date'
            ? fmtDate(raw)
            : String(raw == null ? '' : raw).trim();
        return { label, val };
      })
      // Keep a row even if empty so the family can fill it in by hand, but
      // only include the whole section if at least one value is present.
      ;
    const anyFilled = rows.some((r) => r.val);
    const headerClass = anyFilled ? '' : 'section-empty';
    return `
      <section class="review-section ${headerClass}">
        <h2>${escapeHtml(section.title)}</h2>
        <table>
          <tbody>
            ${rows
              .map(
                (r) => `
              <tr>
                <th scope="row">${escapeHtml(r.label)}</th>
                <td>${r.val ? escapeHtml(r.val).replace(/\n/g, '<br>') : '<span class="blank">—</span>'}</td>
              </tr>`
              )
              .join('')}
          </tbody>
        </table>
      </section>`;
  }).join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: "Helvetica Neue", Arial, sans-serif;
    color: #1f2937;
    margin: 0;
    padding: 32px 40px;
    font-size: 12pt;
    line-height: 1.45;
  }
  header {
    border-bottom: 2px solid #1e3a8a;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }
  header .brand {
    font-size: 11pt;
    color: #1e3a8a;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 700;
    margin-bottom: 4px;
  }
  header h1 {
    font-size: 20pt;
    margin: 0;
    color: #0f172a;
  }
  header .subtitle {
    margin-top: 6px;
    color: #475569;
    font-size: 10.5pt;
  }
  .review-section {
    margin-top: 18px;
    break-inside: avoid;
  }
  .review-section h2 {
    font-size: 12pt;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #1e3a8a;
    border-bottom: 1px solid #cbd5e1;
    padding-bottom: 4px;
    margin: 0 0 8px 0;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th, td {
    text-align: left;
    vertical-align: top;
    padding: 6px 8px;
    border-bottom: 1px dotted #cbd5e1;
  }
  th {
    width: 34%;
    font-weight: 600;
    color: #334155;
  }
  td .blank { color: #94a3b8; }
  .signature-block {
    margin-top: 28px;
    padding-top: 14px;
    border-top: 2px solid #1e3a8a;
  }
  .signature-block .sig-row {
    display: flex;
    gap: 32px;
    margin-top: 22px;
  }
  .signature-block .sig-row > div { flex: 1; }
  .signature-block .sig-line {
    border-bottom: 1px solid #0f172a;
    height: 36px;
  }
  .signature-block .sig-label {
    font-size: 9.5pt;
    color: #475569;
    margin-top: 4px;
  }
  .note {
    margin-top: 16px;
    padding: 10px 14px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 10pt;
    color: #475569;
  }
  @page { margin: 0.6in; }
  @media print {
    body { padding: 0; }
    .no-print { display: none !important; }
  }
  .print-bar {
    position: sticky;
    top: 0;
    background: #fff;
    padding: 10px 0;
    border-bottom: 1px solid #e2e8f0;
    margin: -10px 0 18px 0;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
  .print-bar button {
    background: #1e3a8a;
    color: #fff;
    border: 0;
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 11pt;
    cursor: pointer;
  }
  .print-bar button.secondary {
    background: #fff;
    color: #1e3a8a;
    border: 1px solid #1e3a8a;
  }
</style>
</head>
<body>
  <div class="print-bar no-print">
    <button type="button" class="secondary" onclick="window.close()">Close</button>
    <button type="button" onclick="window.print()">Print</button>
  </div>
  <header>
    ${brand ? `<div class="brand">${escapeHtml(brand)}</div>` : ''}
    <h1>${escapeHtml(title)}</h1>
    <div class="subtitle">Please review every field below and make any corrections. We will use this information to draft the obituary.</div>
  </header>
  ${sectionsHtml}
  <div class="signature-block">
    <div class="note">
      By signing, you confirm the information above is accurate to the best of your knowledge and authorize us to use it to draft the obituary.
    </div>
    <div class="sig-row">
      <div>
        <div class="sig-line"></div>
        <div class="sig-label">Family member signature</div>
      </div>
      <div>
        <div class="sig-line"></div>
        <div class="sig-label">Printed name</div>
      </div>
      <div>
        <div class="sig-line"></div>
        <div class="sig-label">Date</div>
      </div>
    </div>
  </div>
  <script>
    // Auto-open print dialog shortly after load so the employee just needs to confirm.
    window.addEventListener('load', function () {
      setTimeout(function () { try { window.print(); } catch (e) {} }, 250);
    });
  </script>
</body>
</html>`;
}

export default function printObituaryReview(formData, options = {}) {
  const html = buildReviewHtml(formData, options);
  // Use a Blob URL so the new tab has a real, navigable URL (works more
  // reliably across browsers and sandboxed contexts than document.write
  // into an empty about:blank window).
  let objectUrl = null;
  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    objectUrl = URL.createObjectURL(blob);
  } catch (e) {
    objectUrl = null;
  }

  if (objectUrl) {
    const w = window.open(objectUrl, '_blank');
    if (w) {
      // Revoke after a few minutes so the tab has time to load.
      setTimeout(() => { try { URL.revokeObjectURL(objectUrl); } catch (e) {} }, 5 * 60 * 1000);
      return;
    }
    try { URL.revokeObjectURL(objectUrl); } catch (e) {}
  }

  // Popups blocked OR blob creation failed → fall back to a hidden iframe
  // and trigger the print dialog inline.
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {}
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 300);
}
